import { useTranslation } from "react-i18next";
import { Box, Button, Divider } from "@mui/material";

import { GO_SERVICE_QUERY_KEYS } from "../../../../../api/queryKeys";
import { ServiceRequest } from "../../../../../types/service.type";
import { StripePayModal } from "../../../../../components/StripePayModal";
import { getQueryKey } from "../../../../../api/queries/useServiceFeedback";
import { ContractPaymentData } from "../../../../../api/types/contract.types";

import ReviewService from "./ReviewService";

interface ContractActionsProps {
  canReview: boolean;
  canProceedToPayment: boolean;
  serviceRequest: ServiceRequest;
  isProvider: boolean;
  isCreatingPayment: boolean;
  paymentData: ContractPaymentData | null;
  onOpenPaymentModal: () => void;
  onPaymentSuccess: () => void;
  stripeModalProps: {
    open: boolean;
    closeModal: () => void;
    clientSecret: string | null;
    publishableKey: string | null;
  };
}

export const ContractActions = ({
  canReview,
  canProceedToPayment,
  serviceRequest,
  isProvider,
  isCreatingPayment,
  paymentData,
  onOpenPaymentModal,
  onPaymentSuccess,
  stripeModalProps,
}: ContractActionsProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });

  if (!canReview && !canProceedToPayment) {
    return null;
  }

  const showReview = canReview && serviceRequest.canReview !== false;
  const showPayment = canProceedToPayment;

  return (
    <>
      {(showReview || showPayment) && <Divider sx={styles.divider} />}
      <Box sx={styles.buttonsContainer}>
        {showReview && (
          <ReviewService
            requestId={serviceRequest.id}
            serviceId={serviceRequest.service.id}
            userId={serviceRequest.serviceProvider!?.id}
            serviceType={serviceRequest.service.type}
            isProvider={isProvider}
          />
        )}
        {showPayment && (
          <>
            <Button
              variant="primary-md"
              sx={styles.continueToPaymentButton}
              onClick={onOpenPaymentModal}
              disabled={isCreatingPayment || !paymentData}
            >
              {t("continueToPayment")}
            </Button>
            <StripePayModal
              open={stripeModalProps.open}
              closeModal={stripeModalProps.closeModal}
              clientSecret={stripeModalProps.clientSecret}
              publishableKey={stripeModalProps.publishableKey}
              queryKey={
                getQueryKey({
                  userId: serviceRequest.serviceProvider?.id,
                  serviceId: serviceRequest.service.id,
                  requestId: serviceRequest.id,
                }) as string[]
              }
              additionalQueryKeys={
                serviceRequest.contractId
                  ? [[...GO_SERVICE_QUERY_KEYS.contractDetails(serviceRequest.contractId)]]
                  : undefined
              }
              onSuccess={onPaymentSuccess}
            />
          </>
        )}
      </Box>
    </>
  );
};

const styles = {
  divider: {
    mx: "-16px",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
  continueToPaymentButton: {
    width: "fit-content",
  },
};
